import { useEffect, useState } from 'react';
import TapSurface from './TapSurface';
import { useBetSlip } from '../contexts/BetSlipContext';
import { useTheme } from '../contexts/ThemeContext';

// Special one-night board card: MLB Home Run Derby winner market.
// Self-hides when /api/games/derby reports unavailable (off-season, expired,
// or the upstream market vanished). Betting locks at first pitch.
//
// Styled in the cartoon/arcade "sticker" theme shared with the battle summary,
// profile page, and LiveBattleStoryViewer: a theme-aware sticker card (2.5px
// solid ink border + 4px hard offset shadow) with a warm dotted header band.
// It renders only after a client fetch resolves (returns null before that), so
// it's never part of first paint — useTheme() inline styling here can't FOUC.
const POLL_MS = 60 * 1000;

const formatOdds = (o) => {
  const n = Number(o);
  if (!Number.isFinite(n)) return '-';
  return n > 0 ? `+${n}` : `${n}`;
};

export default function DerbyCard() {
  const { addToBetSlip, isBetInSlip } = useBetSlip();
  const { theme } = useTheme();
  const isLight = theme === 'light';
  const [market, setMarket] = useState(null);

  useEffect(() => {
    let cancelled = false;
    // Dev-only visual test hook: /?derbyMock=1 (the API ignores mock in prod).
    const mock =
      typeof window !== 'undefined' &&
      new URLSearchParams(window.location.search).get('derbyMock') === '1';
    const load = () => {
      fetch(mock ? '/api/games/derby?mock=1' : '/api/games/derby')
        .then((r) => (r.ok ? r.json() : null))
        .then((data) => {
          if (cancelled || !data) return; // transient failure: keep last good
          setMarket(data.available ? data : null);
        })
        .catch(() => {}); // network blip: keep showing the last good market
    };
    load();
    const iv = setInterval(load, POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(iv);
    };
  }, []);

  if (!market) return null;

  const derbyGame = {
    id: market.id,
    matchupName: market.title,
    awayTeam: '',
    homeTeam: '',
    awayTeamFull: market.title,
    homeTeamFull: market.title,
    sportName: 'MLB',
    sport: 'baseball_mlb',
    startTime: market.startTime,
    time: market.time || '',
  };

  const locked = !!market.locked;

  // Cartoon/arcade sticker theme — theme-aware for light + dark.
  const INK = isLight ? '#0a0a0a' : '#f8fafc';
  const STICKER_BG = isLight ? '#ffffff' : '#0f172a';
  const INNER_BG = isLight ? '#f8fafc' : '#111827';
  const MUTED = isLight ? '#78716c' : '#94a3b8';
  const ODDS_ACCENT = isLight ? '#1d4ed8' : '#60a5fa';

  return (
    <div
      className="rounded-2xl overflow-hidden mb-3 lbsv-anim"
      style={{
        backgroundColor: STICKER_BG,
        border: `2.5px solid ${INK}`,
        boxShadow: `4px 4px 0 ${INK}`,
        animation: 'lbsvPop 0.5s cubic-bezier(0.22,1.4,0.4,1) both',
      }}
    >
      {/* Warm dotted header band — the orange gradient is fixed in both
          themes, so text/borders sitting on it use literal dark ink #0a0a0a. */}
      <div
        className="flex items-center justify-between gap-2 px-3.5 py-2.5"
        style={{
          backgroundColor: '#f59e0b',
          backgroundImage:
            'radial-gradient(rgba(10,10,10,0.12) 1.3px, transparent 1.3px), linear-gradient(135deg, #fb923c 0%, #f59e0b 100%)',
          backgroundSize: '13px 13px, 100% 100%',
          borderBottom: '2.5px solid #0a0a0a',
        }}
      >
        <div className="flex items-center gap-2.5 min-w-0">
          <span
            className="flex items-center justify-center rounded-full flex-shrink-0"
            style={{
              width: 30,
              height: 30,
              backgroundColor: '#ffffff',
              border: '2px solid #0a0a0a',
              boxShadow: '2px 2px 0 #0a0a0a',
            }}
          >
            <span className="text-base leading-none">🏆</span>
          </span>
          <div className="min-w-0">
            <div
              className="text-sm font-black uppercase tracking-wide truncate"
              style={{ color: '#0a0a0a' }}
            >
              Home Run Derby
            </div>
            <div className="text-[11px] font-bold truncate" style={{ color: '#7c2d12' }}>
              {locked ? 'Betting closed — event underway' : `Tonight • ${market.time || ''} • Winner`}
            </div>
          </div>
        </div>
        <span
          className="text-[10px] font-black uppercase tracking-wider px-2 py-1 rounded-lg flex-shrink-0"
          style={{
            color: '#0a0a0a',
            backgroundColor: '#facc15',
            border: '2px solid #0a0a0a',
            boxShadow: '2px 2px 0 #0a0a0a',
          }}
        >
          MLB Special
        </span>
      </div>

      {/* Player tiles */}
      <div
        className="grid grid-cols-2 sm:grid-cols-4 gap-2 p-3"
        style={{ backgroundColor: STICKER_BG }}
      >
        {market.players.map((p) => {
          const selection = `Derby Winner: ${p.name}`;
          const active = isBetInSlip(derbyGame, 'gp:derby_winner', selection);
          if (locked) {
            return (
              <div
                key={p.name}
                className="rounded-xl text-center py-2 px-1.5"
                style={{ backgroundColor: INNER_BG, border: `2px solid ${INK}`, opacity: 0.55 }}
              >
                <div className="text-[11px] font-bold truncate" style={{ color: MUTED }}>
                  {p.name}
                </div>
                <div className="text-[13px] font-black" style={{ color: MUTED }}>
                  {formatOdds(p.odds)}
                </div>
              </div>
            );
          }
          return (
            <TapSurface
              key={p.name}
              optimisticToggle
              onTap={() => addToBetSlip(derbyGame, 'gp:derby_winner', p.odds, selection)}
              isActive={active}
              activeColor="#3b82f6"
              inactiveColor={INNER_BG}
              style={{
                borderRadius: '12px',
                padding: '8px 6px',
                textAlign: 'center',
                border: `2px solid ${INK}`,
                boxShadow: active ? `2.5px 2.5px 0 ${INK}` : `2px 2px 0 ${INK}`,
              }}
            >
              <div
                className="text-[11px] font-bold truncate"
                style={{ color: active ? '#ffffff' : INK, marginBottom: '1px' }}
              >
                {p.name}
              </div>
              <div
                className="text-[13px] font-black"
                style={{ color: active ? '#ffffff' : ODDS_ACCENT }}
              >
                {formatOdds(p.odds)}
              </div>
            </TapSurface>
          );
        })}
      </div>
    </div>
  );
}
